import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import KeyData from "@/components/KeyData";

export default function RevealLifeMap() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.post("/reveal/generate").then(r => setData(r.data)).catch(() => {
      // Might already exist; try fetching via /journey/phase-map
      api.get("/journey/phase-map").then(r => {
        setData({ chart: { mahadashas: r.data.mahadashas, current_chapter_index: r.data.current_index } });
      });
    });
  }, []);

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center constellation-bg"><span className="animate-soft-pulse" style={{ color: "var(--vedari-ink-soft)" }}>A moment…</span></div>;
  }

  const mds = data.chart?.mahadashas || [];
  const currentIdx = data.chart?.current_chapter_index ?? 0;
  const current = mds[currentIdx];

  return (
    <div className="min-h-screen constellation-bg pb-8" style={{ color: "var(--vedari-ink)" }}>
      <div className="px-6 pt-12 pb-8 max-w-md mx-auto">
        <p className="text-caption tracking-[0.25em] uppercase" style={{ color: "var(--vedari-gold)" }} data-testid="lifemap-eyebrow">Your Life Map</p>
        <h1 className="font-display text-4xl mt-3 leading-tight">Your life unfolds in chapters.</h1>
        <p className="text-body-lg mt-4 leading-relaxed" style={{ color: "var(--vedari-ink-soft)" }}>
          Vedic astrology calls them Mahadashas. There are <KeyData>9</KeyData> in every life. You are in chapter <KeyData>{currentIdx + 1}</KeyData>.
        </p>
      </div>

      <div className="overflow-x-auto no-scrollbar pb-4" data-testid="lifemap-scroll">
        <div className="inline-flex items-center gap-0 px-6 min-w-full">
          {mds.map((m, i) => {
            const isCurrent = i === currentIdx;
            const isPast = i < currentIdx;
            return (
              <div key={i} className="flex items-center">
                <div
                  className={`flex flex-col items-center px-3 transition-opacity ${isPast ? "opacity-60" : ""} ${i > currentIdx ? "opacity-40" : ""}`}
                  data-testid={`mahadasha-node-${i}`}
                >
                  <div
                    className={`w-14 h-14 rounded-full flex items-center justify-center font-serif text-h2 transition-all ${isCurrent ? "animate-glow-pulse" : ""}`}
                    style={{
                      backgroundColor: isCurrent ? "var(--vedari-surface)" : "transparent",
                      border: `${isCurrent ? "2px" : "1.5px"} solid ${isCurrent ? "var(--vedari-gold)" : "var(--vedari-divider)"}`,
                      color: isCurrent ? "var(--vedari-gold)" : "var(--vedari-ink)",
                    }}
                  >
                    {m.planet[0]}
                  </div>
                  <p className="mt-3 text-body-sm font-medium" style={{ color: isCurrent ? "var(--vedari-gold)" : "var(--vedari-ink)" }}>{m.planet}</p>
                  <p className="text-caption mt-1 key-data" style={{ opacity: 0.75 }}>
                    {m.start_date.slice(0, 4)}–{m.end_date.slice(0, 4)}
                  </p>
                </div>
                {i < mds.length - 1 && (
                  <span className="h-px w-6" style={{ backgroundColor: "var(--vedari-divider)" }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {current && (
        <div className="px-6 pt-10 max-w-md mx-auto">
          <div className="vedari-card">
            <p className="text-caption" style={{ color: "var(--vedari-ink-soft)" }}>You are now in your</p>
            <h2 className="font-display text-h1 mt-1"><KeyData>{current.planet}</KeyData> Mahadasha</h2>
            <p className="text-body mt-4" style={{ color: "var(--vedari-ink-soft)" }}>
              Started: <KeyData>{formatDate(current.start_date)}</KeyData><br/>
              Ends: <KeyData>{formatDate(current.end_date)}</KeyData>
            </p>
            <p className="text-body-lg mt-4 leading-relaxed">
              This is your chapter of <span className="font-serif italic" style={{ color: "var(--vedari-gold)" }}>{planetTheme(current.planet)}</span>.
            </p>
          </div>
          <button onClick={() => navigate("/reveal/chapter")} className="vedari-btn-primary w-full mt-8" data-testid="lifemap-continue-btn">
            Continue
          </button>
        </div>
      )}
    </div>
  );
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function planetTheme(p) {
  const themes = {
    Ketu: "release and inward seeking",
    Venus: "love, beauty and partnerships",
    Sun: "authority and identity",
    Moon: "emotion and family",
    Mars: "action and courage",
    Rahu: "ambition and expansion",
    Jupiter: "wisdom and grace",
    Saturn: "discipline and slow building",
    Mercury: "voice and communication",
  };
  return themes[p] || p;
}
